import React from 'react';
import { Text, View, StyleSheet, TouchableOpacity, Animated, Platform } from 'react-native';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import _ from 'lodash';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMyTimeTableStore } from 'src/domain/stores/useMyTimeTableStore';
import Translations from 'src/core/utils/Translations';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import { formatTimetableRange, resolveTimetableSeconds } from 'src/core/utils/formatTimetableTime';
import type { ApiTimetableItem } from 'src/core/models/api/shared/ApiTimetable';
import type { TimeTablePageItem } from 'src/presentation/navigation/types/NavigationTypes';

interface TimetableRowProps {
	scrollPos: Animated.Value;
	eventUuid: string;
	dayTitle: string;
	offset: number;
	stageTitle: string;
	title: string;
	startdate: number;
	enddate: number;
	color: string;
	items: ApiTimetableItem[];
	onItemPress: (item: TimeTablePageItem, title: string) => void;
}

function TimetableRow({
	scrollPos,
	eventUuid,
	dayTitle,
	title,
	startdate,
	color,
	items,
	onItemPress,
}: TimetableRowProps) {
	const eventStore = useEventStore();

	const showItem = (data: TimeTablePageItem) => {
		onItemPress(data, dayTitle);
	};

	// Resolve every session's position up front: a legacy session without timestamps is placed by its { HH, mm }
	// clock, and the next block's margin is measured against that resolved end.
	const positioned = items.map((item) => ({
		item,
		start: resolveTimetableSeconds(item.startdate, item.start, startdate),
		end: resolveTimetableSeconds(item.enddate, item.end, startdate),
	}));

	const renderItems = () => {
		return positioned.map(({ item, start, end }, index) => {
			const blockWidth = start !== null && end !== null ? Math.max((end - start) / 10, 0) : 0;
			let blockMarginLeft = 0;

			if (start === null) {
				// No usable time at all: keep it in the row instead of pushing it off the axis.
				blockMarginLeft = 0;
			} else if (index === 0) {
				const timetableStart = startdate - (startdate % 3600);
				blockMarginLeft = (start - timetableStart) / 10 + 180;
			} else {
				const previousEnd = positioned[index - 1]!.end;
				blockMarginLeft = previousEnd !== null ? (start - previousEnd) / 10 : 0;
			}

			const data = {
				...item,
				stageTitle: title,
				navTitle: dayTitle,
				eventUuid,
			};

			return (
				<TouchableOpacity
					key={`${item.title}-${item.startdate}`}
					onPress={_.debounce(() => showItem(data), 800, {
						leading: true,
						trailing: false,
					})}
				>
					<View
						style={[
							styles.block,
							{
								marginLeft: blockMarginLeft,
								backgroundColor: color,
								width: blockWidth,
							},
						]}
					>
						<View style={styles.blockTimeRow}>
							<Text style={styles.blockTime}>{formatTimetableRange(item)}</Text>
							{item.page_id ? <BlockFavorite eventUuid={eventUuid} pageId={item.page_id} /> : null}
						</View>
						{/* A block is only 70 high, so a long title was cut off mid-letter; two lines end in an ellipsis. */}
						<Text numberOfLines={2} style={styles.blockTitle}>
							{item.title}
						</Text>
					</View>
				</TouchableOpacity>
			);
		});
	};

	return (
		// No flex here: this row sits inside a vertical ScrollView that itself sits inside the horizontal one,
		// which stretches its content to the full height.
		<View>
			<Animated.View style={{ transform: [{ translateX: scrollPos }] }}>
				<Text style={[styles.stageTitle, { color: eventStore.activeTheme.heading_text_color }]}>
					{title?.toUpperCase()}
				</Text>
			</Animated.View>
			<View style={{ flexDirection: 'row' }}>{renderItems()}</View>
		</View>
	);
}

/**
 * A block that mirrors a program item is liked as that page, not as a session: the same favourite the program
 * list and Mijn timetable use (#416).
 */
function BlockFavorite({ pageId, eventUuid }: { pageId: number; eventUuid: string }) {
	const favorite = useMyTimeTableStore((state) => state.myTimeTableData.some((page) => page.pageid === pageId));
	const favoritesDisabled = useMyTimeTableStore((state) => state.loadFailed);
	const toggleFavorite = useMyTimeTableStore((state) => state.toggleFavorite);

	return (
		<TouchableOpacity
			accessibilityLabel={Translations.buttonFavorite}
			accessibilityRole="button"
			accessibilityState={{ selected: favorite }}
			hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
			style={[styles.blockFavorite, favoritesDisabled ? styles.blockFavoriteDisabled : null]}
			onPress={() => {
				toggleFavorite(pageId, eventUuid).catch(() => undefined);
			}}
		>
			{Platform.OS === 'ios' ? (
				<Ionicons color={favorite ? Colors.red : Colors.white} name={favorite ? 'heart' : 'heart-outline'} size={18} />
			) : (
				<MaterialIcons color={favorite ? Colors.red : Colors.white} name="favorite" size={18} />
			)}
		</TouchableOpacity>
	);
}

export default TimetableRow;

const styles = StyleSheet.create({
	block: {
		height: 70,
		paddingHorizontal: Dimens.padding20,
		borderRightWidth: 1,
		borderRightColor: Colors.white,
		justifyContent: 'center',
		alignItems: 'flex-start',
	},
	stageTitle: {
		marginLeft: Dimens.margin15,
		marginTop: Dimens.margin20,
		marginBottom: 7,
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.montserratBold,
	},
	blockTimeRow: {
		flexDirection: 'row',
		alignItems: 'center',
		// The heart sits at the far end of the block instead of crowding the time.
		alignSelf: 'stretch',
		justifyContent: 'space-between',
	},
	blockFavorite: {
		marginLeft: Dimens.margin8,
	},
	blockFavoriteDisabled: {
		opacity: 0.35,
	},
	blockTime: {
		color: Colors.white,
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.montserratRegular,
	},
	blockTitle: {
		color: Colors.white,
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.montserratBold,
		alignSelf: 'stretch',
	},
});
